import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { Link } from "@/components/catalyst/link";
import { LinkIcon } from "lucide-react";
import { useTranslations } from "use-intl";
import {
  InvoiceLineItemFieldsFragment,
  InvoicePaymentFieldsFragment,
  InvoicePaymentType,
  ModelType,
} from "@/gql/graphql";
import { InvoiceRecord } from "@/app/Invoices/GraphQL/invoiceRecordQuery";
import { formatDate } from "@/lib/Formatters/formatDate";
import { InvoicePaymentTag } from "@/app/Invoices/Components/InvoicePaymentFeed";
import { cn } from "@/lib/utils";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";
import { useInvoicePaymentActions } from "@/app/Invoices/Hooks/useInvoicePaymentActions";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { InvoicePaymentDialog } from "@/app/Invoices/Components/InvoicePaymentDialog";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";

export function PayableLineItemTable({ invoice }: { invoice: InvoiceRecord }) {
  const t = useTranslations("invoice");
  const { open } = useDialog(InvoicePaymentDialog);
  const { isAdmin } = useTenant();

  return (
    <table className="min-w-full divide-y divide-gray-300">
      <thead>
        <tr>
          <th
            scope="col"
            className="py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 md:pl-0"
          >
            Description
          </th>
          <th
            scope="col"
            className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 sm:table-cell"
          >
            Qty
          </th>
          <th
            scope="col"
            className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 sm:table-cell"
          >
            Rate
          </th>
          <th
            scope="col"
            className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 sm:table-cell"
          >
            Tax
          </th>
          <th
            scope="col"
            className="py-3.5 pl-3 pr-4 text-right text-sm font-semibold text-gray-900 sm:pr-6 md:pr-0"
          >
            Amount
          </th>
        </tr>
      </thead>
      <tbody>
        {invoice.lineItems.map((lineItem) => {
          return (
            <tr key={lineItem.id} className="border-b border-gray-200">
              <td className="py-4 pr-3 text-sm sm:pl-6 md:pl-0">
                <div className="font-medium text-gray-900">
                  {lineItem.description}
                </div>
                <div>
                  {lineItem.fulfills ? (
                    <PaymentAttributionLink lineItem={lineItem} />
                  ) : null}
                </div>
                <div className="mt-0.5 text-gray-500 sm:hidden">
                  {lineItem.quantity} {lineItem.unit_amount} at{" "}
                  {formatCurrency(lineItem.unit_amount, invoice.currency)}
                </div>
              </td>
              <td className="hidden px-3 py-4 text-right text-sm text-gray-500 sm:table-cell">
                {lineItem.quantity}
              </td>
              <td className="hidden px-3 py-4 text-right text-sm text-gray-500 sm:table-cell">
                {formatCurrency(lineItem.unit_amount, invoice.currency)}
              </td>
              <td className="hidden px-3 py-4 text-right text-sm text-gray-500 sm:table-cell">
                {t(`tax_type.${lineItem.tax_type}`)}
              </td>
              <td className="py-4 pl-3 pr-4 text-right text-sm text-gray-500 sm:pr-6 md:pr-0">
                {formatCurrency(
                  lineItem.unit_amount * lineItem.quantity,
                  invoice.currency,
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <th
            scope="row"
            colSpan={4}
            className="pl-6 pr-3 pt-6 text-right text-sm font-normal text-gray-500 md:pl-0"
          >
            Subtotal
          </th>
          <td className="pl-3 pr-4 pt-6 text-right text-sm text-gray-500 sm:pr-6 md:pr-0">
            {formatCurrency(invoice.amount_exc_tax, invoice.currency)}
          </td>
        </tr>
        <tr>
          <th
            scope="row"
            colSpan={4}
            className="pl-6 pr-3 pt-4 text-right text-sm font-normal text-gray-500 md:pl-0"
          >
            GST
          </th>
          <td className="pl-3 pr-4 pt-4 text-right text-sm text-gray-500 sm:pr-6 md:pr-0">
            {formatCurrency(invoice.amount_tax, invoice.currency, {
              showZero: true,
            })}
          </td>
        </tr>
        <tr>
          <th
            scope="row"
            colSpan={4}
            className="py-4 pl-6 pr-3 text-right text-sm font-semibold text-gray-900 md:pl-0"
          >
            Total
          </th>
          <td className="py-4 pl-3 pr-4 text-right text-sm font-semibold text-gray-900 sm:pr-6 md:pr-0">
            {formatCurrency(invoice.amount_inc_tax, invoice.currency)}
          </td>
        </tr>

        {invoice.invoicePayments.length > 0 ? (
          <>
            <tr>
              <td colSpan={5} className="py-2">
                <div className="border-t border-gray-200" />
              </td>
            </tr>
            {invoice.invoicePayments.map((payment) => (
              <PaymentRow
                key={payment.id}
                payment={payment}
                invoice={invoice}
              />
            ))}
          </>
        ) : null}

        {isAdmin ? (
          <tr>
            <td
              colSpan={5}
              className="py-2 text-right text-xs text-blue-500 underline"
            >
              <button
                onClick={() => {
                  open({
                    invoiceId: invoice.id,
                  });
                }}
              >
                add payment
              </button>
            </td>
          </tr>
        ) : null}

        <tr className="border-t-2 border-gray-900">
          <th
            scope="row"
            colSpan={4}
            className="pl-6 pr-3 pt-6 text-right text-base font-bold text-gray-900 sm:pl-6 md:pl-0"
          >
            Amount Due
          </th>
          <td className="pl-3 pr-4 pt-6 text-right text-base font-bold text-gray-900 sm:pr-6 md:pr-0">
            {formatCurrency(invoice.amount_due, invoice.currency, {
              showZero: true,
            })}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

function PaymentRow({
  payment,
  invoice,
}: {
  payment: InvoicePaymentFieldsFragment;
  invoice: InvoiceRecord;
}) {
  const isRefund = payment.type === InvoicePaymentType.Refund;
  const getActions = useInvoicePaymentActions();

  return (
    <tr>
      <td
        colSpan={4}
        className="p-2 pl-6 pr-3 text-right text-sm text-gray-600 sm:pl-6 md:pl-0"
      >
        <div className="flex items-center justify-end gap-2">
          <IconMenuActions actions={getActions(payment)} />

          <InvoicePaymentTag payment={payment} />
          <span>{payment.reference}</span>
          {payment.description ? (
            <span className="text-gray-500">- {payment.description}</span>
          ) : null}
          <span className="text-gray-400">
            ({formatDate(payment.payment_date)})
          </span>
        </div>
      </td>
      <td
        className={cn(
          "pl-3 pr-4 pt-2 text-right text-sm sm:pr-6 md:pr-0",
          isRefund ? "text-red-600" : "text-gray-600",
        )}
      >
        <div className="flex items-center justify-end gap-2">
          <span>
            {isRefund ? "-" : ""}
            {formatCurrency(payment.amount, invoice.currency)}
          </span>
        </div>
      </td>
    </tr>
  );
}

function PaymentAttributionLink({
  lineItem,
}: {
  lineItem: InvoiceLineItemFieldsFragment;
}) {
  if (
    !lineItem.fulfills?.attributed_to_type ||
    !lineItem.fulfills?.attributed_to_id
  ) {
    return null;
  }

  if (lineItem.fulfills.attributed_to_type === ModelType.Booking) {
    const bookingId = lineItem.fulfills.attributed_to_id;

    return (
      <Link
        from="/$tenant/$orgId/supplier/{-$supplierId}"
        to={"/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId"}
        params={(prev) => ({
          ...prev,
          bookingId,
        })}
        className="text-xs text-blue-500 underline"
      >
        <LinkIcon className="mr-2 inline h-4 w-4" />
        {`ORD${bookingId.padStart(6, "0")}`}
      </Link>
    );
  }

  throw new Error(
    `Unsupported attributed_to_type ${lineItem.fulfills.attributed_to_type}`,
  );
}
